/**
 * # dxfBridge.conformantSkeleton.emittedEntities — the emitted-entity walk
 *
 * Single source of truth for WHICH typed entities the from-scratch conformant
 * export emits: every top-level space EXCEPT the transient `block-test`
 * (TESTBLOCK sandbox) / `block-edit` (BEDIT working canvas) spaces — which are
 * never written to the DXF (mirrors the exporter's space exclusion in
 * `dxfBridge.export.ts`) — followed by the children of every block whose OWN
 * typed children the BLOCKS section actually emits. T9b review round 2 (P2):
 * the exporter (`buildBlocksSectionFromDrawingBlocks`) drops the typed
 * children of `*Model_Space` / the first `*Paper_Space` / a PAIRED extra tab
 * (the paired SPACE is authoritative), so walking them here minted orphan
 * `*D<n>` blocks / STYLE records and advanced `$HANDSEED` on every save. The
 * block set is taken from the exporter's own predicate
 * (`blockEmitsOwnTypedChildren` over the paper-space layout plan) — never a
 * re-implementation. Shared by the passes that pre-scan the document for
 * records the export will need and by the skeleton's entity remint, so they
 * can never disagree with each other, or with the export, on the entity set.
 */
import {
  blockEmitsOwnTypedChildren,
  buildPaperSpaceLayoutPlan,
} from "./dxfBridge.paperSpaceLayout";
import type { BlockDefinition, DrawingDocument } from "./model";

/**
 * The `[name, block]` entries of `document.blocks` whose OWN typed children the
 * BLOCKS-section export emits, in `document.blocks` order.
 */
export function emittedBlockEntries(
  document: DrawingDocument,
): Array<[string, BlockDefinition]> {
  const { paperSpaceByBlockName } = buildPaperSpaceLayoutPlan(document);
  return Object.entries(document.blocks ?? {}).filter(([name]) =>
    blockEmitsOwnTypedChildren(name, paperSpaceByBlockName),
  );
}

/**
 * Call `visit` once per emitted entity list, in export order: the emitted
 * spaces' entities first, then each emitted block's children.
 */
export function visitEmittedEntityLists(
  document: DrawingDocument,
  visit: (entities: readonly unknown[]) => void,
): void {
  for (const space of document.spaces) {
    if (space.kind === "block-test" || space.kind === "block-edit") continue;
    visit(space.entities);
  }
  for (const [, block] of emittedBlockEntries(document)) visit(block.entities);
}
